import { V2 } from "@ui/theme/DesignV2";
import { Box } from "@ui/box/Box";
import { Spinner } from "@ui/spinner/Spinner";
import { RecursiveCSS } from "@ui/theme/GlobalTheme";
import { FC, PropsWithChildren } from "react";

type Props = PropsWithChildren & {
	busy?: boolean;
};

export const StartScreenView: FC<Props> = ({ busy, children }) => {
	return (
		<Box
			role="main"
			aria-label="Secure workspace access"
			sx={{
				position: "fixed",
				inset: 0,
				zIndex: 1000,
				width: "100%",
				height: "100dvh",
				minWidth: 0,
				boxSizing: "border-box",
				backgroundColor: V2.canvas,
				backgroundImage:
					"none",
				color: "#152331",
				overflowX: "hidden",
				overflowY: "auto",
				overscrollBehavior: "contain",
				"&::before": {
					content: "''",
					position: "fixed",
					inset: 0,
					pointerEvents: "none",
					backgroundImage: "none",
					backgroundSize: "48px 48px",
				} as RecursiveCSS,
			}}>
			<Box
				sx={{
					position: "relative",
					zIndex: 1,
					width: "100%",
					minWidth: 0,
					minHeight: "100%",
					boxSizing: "border-box",
					padding:
						"max(22px, var(--safe-area-inset-top, env(safe-area-inset-top, 0px))) clamp(14px, 4vw, 42px) max(22px, var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))",
					"@media (max-width: 768px)": {
						padding: "max(22px, var(--safe-area-inset-top, env(safe-area-inset-top, 0px))) clamp(14px, 4vw, 42px) max(70px, var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))",
					},
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
				}}>
				<Box
					data-start-screen-stage="true"
					sx={{
						width: "100%",
						maxWidth: "780px",
						minWidth: 0,
						boxSizing: "border-box",
						marginTop: "auto",
						marginBottom: "auto",
						display: "flex",
						justifyContent: "center",
						"& > :not([data-auth-surface='true'])": {
							width: "100%",
							maxWidth: "720px",
							minWidth: "0 !important",
							boxSizing: "border-box",
							backgroundColor: "#FFFFFF",
							border: "1px solid rgba(31, 56, 68, .12)",
							borderRadius: "20px",
							boxShadow: "0 24px 64px rgba(24, 43, 52, .12)",
						} as RecursiveCSS,
						"@media (max-width: 480px)": {
							"& > :not([data-auth-surface='true'])": {
								padding: "28px 20px !important",
								borderRadius: "17px",
							} as RecursiveCSS,
						} as RecursiveCSS,
					}}>
					{children}
				</Box>
				<Box
					role="navigation"
					aria-label="Legal documents and cookie settings"
					data-start-legal
					sx={{
						position: "relative", zIndex: 1, display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: "4px 18px", marginTop: "16px", fontSize: "12px", lineHeight: 1.5,
						"& a, & .cp-cookie-inline": { display: "inline-flex", alignItems: "center", minHeight: "44px", padding: "0", border: "0", borderRadius: "4px", background: "transparent", color: V2.muted, fontFamily: "inherit", fontSize: "inherit", fontWeight: 400, lineHeight: "inherit", textDecoration: "underline", textUnderlineOffset: "3px", cursor: "pointer" },
						"& a:focus-visible, & .cp-cookie-inline:focus-visible": { outline: `2px solid ${V2.accent}`, outlineOffset: "4px" }
					} as RecursiveCSS}>
					<a href="/terms.html">Terms and Conditions</a>
					<a href="/cookies.html">Cookies Policy</a>
					<a href="/privacy.html">Privacy Policy</a>
					<button type="button" className="cp-cookie-inline" data-cookie-settings>Cookie settings</button>
				</Box>
			</Box>

			{busy ? (
				<Box
					role="status"
					aria-label="Loading"
					sx={{
						position: "fixed",
						inset: 0,
						zIndex: 10,
						backgroundColor: "rgba(237,241,239,.72)",
						backdropFilter: "blur(4px)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}>
					<Spinner color={V2.accent} />
				</Box>
			) : null}
		</Box>
	);
};
